import React from 'react';

import { useFilters } from '../../hooks/useFilters';
import { FilterHookOptions } from '../../types';
import { Filters as UIFilters } from './Filters';
import { ConnectedFiltersProps, FilterComponent, FilterProps, Filter } from './types';

/**
 * Connected component in order to simply manage the filtering pattern component.
 * If you need to customise your filters, please use the component `Filter` to create your filters
 * If your filters are simple and there is not much customisation involved, use this component.
 *
 * IMPORTANT: Only use this component if `filters` is static.
 * @family Filters
 * @param FiltersProps
 * @returns Filters
 */
export const ConnectedFilters: React.FC<ConnectedFiltersProps> = ({
    filterDropdownProps,
    beforeFilters,
    children,
    ...hookProps
}) => {
    // eslint-disable-next-line deprecation/deprecation
    const filters = children ? [] : hookProps.filters || [];

    const childrenAsArray = React.Children.toArray(children) as FilterComponent<FilterProps>[];

    /**
     * We iterate through each of the children and retrieve their props. With those props
     * we create an array of filters with specific configurations, so that we can then manage
     * and control all of them from the parent state manager.
     */
    childrenAsArray.forEach((child: FilterComponent<FilterProps>) => {
        if (React.isValidElement(child)) {
            const { props, type } = child;
            const { id, ...restOfProps } = props;

            const { hook } = type as unknown as { hook: Filter['hook'] };

            filters.push({
                id,
                config: restOfProps,
                defaults: restOfProps.defaults as FilterHookOptions,
                hook,
                Component: child,
            });
        }
    });

    const filtersProps = useFilters({
        ...hookProps,
        filters,
    });

    return <UIFilters {...filtersProps} beforeFilters={beforeFilters} filterDropdownProps={filterDropdownProps} />;
};

export { ConnectedFilters as Filters };
export type { ConnectedFiltersProps as FiltersProps };
